import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Box, Paper } from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import DescriptionIcon from '@mui/icons-material/Description';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SpeedIcon from '@mui/icons-material/Speed';
import PaidIcon from '@mui/icons-material/Paid';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import '../styles/dashboard.scss';

// ── COLOR TOKENS ──────────────────────────────────────────────────────────────
export const C = {
  navy: '#564B8D',
  navyMid: '#5E529E',
  navyLight: '#6759B0',
  gold: '#F0B93D',
  goldPale: '#FBEFD1',
  teal: '#0E7D7D',
  coral: '#D95F4B',
  slate: '#4A6FA5',
  sand: '#B89B5E',
  green: '#22863a',
  greenBg: '#e6f4ea',
  amber: '#B45309',
  amberBg: '#FEF3C7',
  red: '#C0392B',
  redBg: '#FDECEA',
  blue: '#1A56B0',
  blueBg: '#EBF0FA',
  bg: '#F4F5F7',
  surface: '#FFFFFF',
  surface2: '#F9FAFB',
  border: '#E2E5EC',
  textPrimary: '#1B2A4A',
  textSecondary: '#5A6478',
  textMuted: '#8E97A8',
};

// ── PILL / STATUS BADGE ───────────────────────────────────────────────────────
const PILL_MAP = {
  green: { background: C.greenBg, color: C.green },
  amber: { background: C.amberBg, color: C.amber },
  red:   { background: C.redBg,   color: C.red },
  blue:  { background: C.blueBg,  color: C.blue },
  navy:  { background: 'rgba(86,75,141,0.1)', color: C.navy },
  teal:  { background: '#e0f5f9', color: '#0E7490' },
  ok:    { background: C.greenBg, color: C.green },
  warn:  { background: C.amberBg, color: C.amber },
  alert: { background: C.redBg,   color: C.red },
  info:  { background: C.blueBg,  color: C.blue },
  yellow: { background: C.amberBg, color: C.amber },
};

export const StatusPill = ({ label, type = 'navy', style = {} }) => (
  <span className={`status-pill status-pill--${PILL_MAP[type] ? type : 'navy'}`} style={style}>
    {label}
  </span>
);

// ── KPI CARD ──────────────────────────────────────────────────────────────────
const KPI_VARIANTS = ['ok', 'warn', 'alert', 'default'];

const ARROW_STRIP_RE = /^[▲▼△▽↑↓→⇧⇩]+\s*/;

const KPI_ICON_RULES = [
  [/aircraft|fleet/i, FlightTakeoffIcon],
  [/lease/i, DescriptionIcon],
  [/expir/i, EventBusyIcon],
  [/term/i, ScheduleIcon],
  [/utili[sz]ation/i, SpeedIcon],
  [/overdue|critical/i, WarningAmberIcon],
  [/receivable|balance|invoice|\$/i, PaidIcon],
];

function getKpiIcon(label) {
  const text = String(label ?? '');
  for (const [pattern, Icon] of KPI_ICON_RULES) {
    if (pattern.test(text)) return Icon;
  }
  return InsightsIcon;
}

function getTrendIcon(deltaType) {
  if (deltaType === 'up') return TrendingUpIcon;
  if (deltaType === 'down') return TrendingDownIcon;
  return TrendingFlatIcon;
}

const KPI_BREAKDOWN_KEY_ORDER = ['msn', 'engine', 'apu', 'ldg', 'other'];

const KPI_BREAKDOWN_LABELS = {
  msn: 'MSN',
  engine: 'Engine',
  apu: 'APU',
  fan: 'FAN',
  hpc: 'HPC',
  hpt: 'HPT',
  lpt: 'LPT',
  ldg: 'LG',
  other: 'Other',
};

const formatKpiBreakdownKey = (key) => {
  const k = String(key).toLowerCase();
  if (KPI_BREAKDOWN_LABELS[k]) return KPI_BREAKDOWN_LABELS[k];
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getKpiBreakdownEntries = (breakdown) => {
  if (!breakdown || typeof breakdown !== 'object') return [];
  const keys = Object.keys(breakdown).filter((k) => {
    if (String(k).toLowerCase() === 'other') return false;
    const v = breakdown[k];
    if (v === null || v === undefined || v === '') return false;
    const n = Number(v);
    if (!Number.isNaN(n) && n === 0) return false;
    return true;
  });
  if (!keys.length) return [];
  const orderIndex = new Map(KPI_BREAKDOWN_KEY_ORDER.map((k, i) => [k, i]));
  const sortedKeys = [...keys].sort((a, b) => {
    const ia = orderIndex.has(String(a).toLowerCase()) ? orderIndex.get(String(a).toLowerCase()) : 999;
    const ib = orderIndex.has(String(b).toLowerCase()) ? orderIndex.get(String(b).toLowerCase()) : 999;
    if (ia !== ib) return ia - ib;
    return String(a).localeCompare(String(b));
  });
  return sortedKeys.map((k) => [k, breakdown[k]]);
};

export const KPICard = ({ label, value, delta, deltaType, variant = 'default', breakdown }) => {
  const variantClass = KPI_VARIANTS.includes(variant) ? variant : 'default';
  const deltaModifier = deltaType === 'up' ? 'up' : deltaType === 'down' ? 'down' : 'flat';
  const breakdownEntries = getKpiBreakdownEntries(breakdown);
  const hasBreakdown = breakdownEntries.length > 0;
  const hasDelta = delta != null && String(delta).trim() !== '';
  const Icon = getKpiIcon(label);
  const TrendIcon = getTrendIcon(deltaType);

  return (
    <Box className={`kpi-card kpi-card--${variantClass}`}>
      <Box className="kpi-card__glow" />

      <Box className="kpi-card__header">
        <Box className="kpi-card__icon-badge">
          <Icon className="kpi-card__icon" />
        </Box>
        <div className="kpi-card__label">
          {label}
        </div>
      </Box>

      <div className={`kpi-card__value${hasBreakdown ? ' kpi-card__value--with-breakdown' : ''}`}>
        {value}
      </div>
      {hasBreakdown && (
        <Box className={`kpi-card__breakdown${hasDelta ? '' : ' kpi-card__breakdown--no-delta'}`}>
          {breakdownEntries.map(([k, v]) => (
            <div key={k} className="kpi-card__breakdown-row">
              <span className="kpi-card__breakdown-label">
                {formatKpiBreakdownKey(k)}
              </span>
              <span className="kpi-card__breakdown-value">
                {typeof v === 'number' ? v : String(v)}
              </span>
            </div>
          ))}
        </Box>
      )}
      <Box className="kpi-card__spacer" />
      {hasDelta && (
        <Box className="kpi-card__delta-wrap">
          <span className={`kpi-card__delta kpi-card__delta--${deltaModifier}`}>
            <TrendIcon className="kpi-card__delta-icon" />
            <span className="kpi-card__delta-text">
              {String(delta).replace(ARROW_STRIP_RE, '')}
            </span>
          </span>
        </Box>
      )}
    </Box>
  );
};

// ── WIDGET CARD ───────────────────────────────────────────────────────────────
export const WidgetCard = ({ title, badge, badgeType, children, noPad = false, style = {}, scrollY = false, maxScrollHeight = 280 }) => (
  <Box className="widget-card" style={style}>
    {/* Header */}
    <Box className="widget-card__header">
      <span className="widget-card__title">
        {title}
      </span>
      {badge && <StatusPill label={badge} type={badgeType || 'info'} />}
    </Box>

    {/* Body */}
    <Box
      className={`widget-card__body${noPad ? ' widget-card__body--no-pad' : ''}${scrollY ? ' widget-card__body--scroll-y' : ''}`}
      style={scrollY ? { maxHeight: maxScrollHeight } : undefined}
    >
      {children}
    </Box>
  </Box>
);

const SECTION_ICON_RULES = [
  [/portfolio/i, SpaceDashboardIcon],
  [/utilization/i, FlightTakeoffIcon],
  [/revenue|cashflow/i, PaidIcon],
  [/expiry/i, EventBusyIcon],
  [/insurance/i, VerifiedUserIcon],
  [/escalation/i, EventRepeatIcon],
  [/alert/i, WarningAmberIcon],
  [/maintenance/i, PrecisionManufacturingIcon],
];

function getSectionIcon(title) {
  const text = String(title ?? '');
  for (const [pattern, Icon] of SECTION_ICON_RULES) {
    if (pattern.test(text)) return Icon;
  }
  return InsightsIcon;
}

// ── SECTION WRAPPER ───────────────────────────────────────────────────────────
export const SectionWrapper = ({ title, subtitle, children }) => {
  const Icon = getSectionIcon(title);
  return (
    <Box className="section-wrapper">
      <Box className="section-wrapper__header">
        <Box className="section-wrapper__icon-badge">
          <Icon className="section-wrapper__icon" />
        </Box>
        <span className="section-wrapper__title">
          {title}
        </span>
        {subtitle && (
          <span className="section-wrapper__subtitle">{subtitle}</span>
        )}
      </Box>
      {children}
    </Box>
  );
};

// ── RISK SCORE BAR ────────────────────────────────────────────────────────────
export const RiskBar = ({ score }) => {
  const fillModifier = score >= 70 ? 'good' : score >= 50 ? 'warn' : 'bad';
  return (
    <Box className="risk-bar">
      <Box className="risk-bar__track">
        <Box className={`risk-bar__fill risk-bar__fill--${fillModifier}`} style={{ width: `${score}%` }} />
      </Box>
      <span className="risk-bar__score">
        {score}
      </span>
    </Box>
  );
};

// ── COVERAGE BAR ──────────────────────────────────────────────────────────────
export const CoverageBar = ({ name, pct, danger }) => (
  <Box className="coverage-bar">
    <span className="coverage-bar__name">{name}</span>
    <Box className="coverage-bar__track">
      <Box className={`coverage-bar__fill${danger ? ' coverage-bar__fill--danger' : ''}`} style={{ width: `${pct}%` }} />
    </Box>
    <span className={`coverage-bar__pct${danger ? ' coverage-bar__pct--danger' : ''}`}>
      {pct}%
    </span>
  </Box>
);

// ── ECHARTS BASE ──────────────────────────────────────────────────────────────
export const EChartBase = ({ option, height = '200px', style = {} }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const instance = echarts.init(ref.current);
    instance.setOption(option);
    const onResize = () => instance.resize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      instance.dispose();
    };
  }, [option]);

  return <div ref={ref} style={{ height, width: '100%', ...style }} />;
};

export const TD_STYLE = {
  padding: '8px 12px',
  borderBottom: `1px solid ${C.border}`,
  color: C.textPrimary,
  verticalAlign: 'middle',
  fontSize: '12px',
};

export const MONO_STYLE = {
  fontFamily: "'DM Mono', 'Roboto Mono', monospace",
  fontSize: '12px',
};

export const DataTable = ({ columns, children, minWidth }) => (
  <Box className="dash-table-scroll">
    <table className="dash-table" style={minWidth ? { minWidth } : undefined}>
      <thead>
        <tr>
          {columns.map((col, i) => {
            const isObject = col && typeof col === 'object';
            const label = isObject ? col.label : col;
            const align = isObject ? col.align : undefined;
            return (
              <th key={i} className="dash-th" style={align ? { textAlign: align } : undefined}>{label}</th>
            );
          })}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </Box>
);

export const DataRow = ({ children, highlight, onClick }) => (
  <tr
    className={`dash-row${highlight ? ` dash-row--${highlight}` : ''}`}
    style={onClick ? { cursor: 'pointer' } : undefined}
    onClick={onClick}
  >
    {children}
  </tr>
);

// ── DONUT LEGEND ITEM ─────────────────────────────────────────────────────────
export const LegendItem = ({ color, label, value }) => (
  <Box className="legend-item">
    <span className="legend-item__dot" style={{ background: color }} />
    <span>{label}</span>
    {value && (
      <span className="legend-item__value">
        {value}
      </span>
    )}
  </Box>
);

// ── GANTT ROW ─────────────────────────────────────────────────────────────────
export const GanttRow = ({ label, left, width, color, text, rowHeight = '22px' }) => (
  <Box className="gantt-row">
    <div className="gantt-row__label">
      {label}
    </div>
    <Box className="gantt-row__track" style={{ height: rowHeight }}>
      <Box className="gantt-row__bar" style={{ left, width, background: color }}>
        {text}
      </Box>
    </Box>
  </Box>
);

// ── SHARED ECHARTS TOOLTIP STYLE ─────────────────────────────────────────────
export const tooltipStyle = {
  backgroundColor: 'rgba(86,75,141,0.92)',
  borderColor: 'transparent',
  textStyle: { color: '#E8EDF5', fontSize: 12 },
  extraCssText: 'border-radius:6px;padding:10px',
};
